import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Video, Radio, Clock3 } from "lucide-react";
import { toast } from "sonner";
import DailyRoomOverlay from "@/components/DailyRoomOverlay";
import JitsiRoomOverlay from "@/components/JitsiRoomOverlay";

const SOON_WINDOW_MIN = 30;

function nowHM() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}
function parseHM(s) {
  const [h, m] = (s || "0:0").split(":").map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
}

// Pick the school-wide class to surface: prefer one that's LIVE now, else the
// soonest one starting within SOON_WINDOW_MIN minutes.
function pickBanner(items) {
  const now = nowHM();
  let live = null;
  let soon = null;
  for (const c of items || []) {
    const s = parseHM(c.start_time);
    const e = parseHM(c.end_time);
    if (now >= s && now <= e) {
      if (!live) live = c;
    } else if (now < s && s - now <= SOON_WINDOW_MIN) {
      if (!soon || parseHM(soon.start_time) > s) soon = c;
    }
  }
  return live || soon;
}

/**
 * School-wide live class banner. Shows across all teacher/admin & student pages
 * when an admin has broadcast a class that is live now or starting soon.
 * Props:
 *  - variant: "staff" | "student"
 *  - displayName: name used inside the video room
 */
export default function LiveClassBanner({ variant = "staff", displayName = "" }) {
  const isStaff = variant === "staff";
  const bannerEndpoint = isStaff ? "/online-classes/live-banner" : "/student/online-classes/live-banner";
  const tokenEndpoint = (id) => (isStaff ? `/online-classes/${id}/daily-token` : `/student/online-classes/${id}/daily-token`);

  const [cls, setCls] = useState(null);
  const [now, setNow] = useState(nowHM());
  const [joining, setJoining] = useState(false);
  const [dailyRoom, setDailyRoom] = useState(null); // { roomUrl, token, subject }
  const [jitsiRoom, setJitsiRoom] = useState(null); // { room, subject }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(bannerEndpoint);
      setCls(pickBanner(data.items));
    } catch { /* silent — banner just won't show */ }
  }, [bannerEndpoint]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  // Tick every 20s so "live/soon" transitions & countdown update without reload.
  useEffect(() => {
    const t = setInterval(() => setNow(nowHM()), 20_000);
    return () => clearInterval(t);
  }, []);

  if (!cls) return null;

  const start = parseHM(cls.start_time);
  const isLive = now >= start && now <= parseHM(cls.end_time);
  const minsToStart = start - now;

  const join = async () => {
    if (cls.provider === "daily") {
      setJoining(true);
      try {
        const { data } = await api.post(tokenEndpoint(cls.id));
        setDailyRoom({ roomUrl: data.room_url, token: data.token, subject: cls.subject });
      } catch (e) {
        toast.error(apiErr(e.response?.data?.detail) || "Could not join the class");
      } finally { setJoining(false); }
    } else if (cls.provider === "jitsi") {
      setJitsiRoom({ room: cls.room_name, subject: cls.subject });
    } else if (cls.meeting_url) {
      window.open(cls.meeting_url, "_blank", "noopener");
    }
  };

  return (
    <>
      <div
        className="relative overflow-hidden rounded-2xl border border-emerald-300/70 dark:border-emerald-800 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 sm:px-5 py-3 mb-6 flex items-center gap-3 sm:gap-4 shadow-sm"
        data-testid="live-class-banner"
        data-status={isLive ? "live" : "soon"}
      >
        <span className="absolute inset-0 pointer-events-none opacity-20 [background-image:radial-gradient(circle_at_20%_20%,white_1px,transparent_1px)] [background-size:16px_16px]" />
        <div className="relative h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <Video className="h-5 w-5" />
        </div>
        <div className="relative min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isLive ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] bg-white/25 rounded-full px-2 py-0.5" data-testid="live-class-badge">
                <Radio className="h-3 w-3 animate-pulse" /> Live now
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] bg-white/25 rounded-full px-2 py-0.5" data-testid="live-class-badge">
                <Clock3 className="h-3 w-3" /> {minsToStart <= 1 ? "Starting now" : `Starts in ${minsToStart} min`}
              </span>
            )}
            <span className="text-[11px] text-white/80 truncate">School-wide class</span>
          </div>
          <div className="font-semibold leading-tight truncate mt-0.5" style={{ fontFamily: "Outfit" }} data-testid="live-class-subject">
            {cls.subject}
            <span className="font-normal text-white/80 text-sm"> · {cls.start_time}–{cls.end_time}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={join}
          disabled={joining}
          className="relative shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-70 text-sm font-semibold px-4 sm:px-5 h-10 transition-colors"
          data-testid="live-class-join-btn"
        >
          <Video className="h-4 w-4" /> {joining ? "Joining…" : "Join now"}
        </button>
      </div>

      {dailyRoom && (
        <DailyRoomOverlay
          roomUrl={dailyRoom.roomUrl}
          token={dailyRoom.token}
          subject={dailyRoom.subject}
          isHost={isStaff}
          onClose={() => setDailyRoom(null)}
        />
      )}
      {jitsiRoom && (
        <JitsiRoomOverlay
          room={jitsiRoom.room}
          subject={jitsiRoom.subject}
          displayName={displayName || (isStaff ? "Teacher" : "Student")}
          isHost={isStaff}
          onClose={() => setJitsiRoom(null)}
        />
      )}
    </>
  );
}
